export const QUALIFICATION_OPTIONS = [
    'O Level', 'A Level', 'Matriculation', 'Intermediate', 'Bachelors', 'Masters', 'Other'
];

export const SUBJECT_GRADE_LEVELS = ['O Level', 'A Level'];
export const PERCENTAGE_LEVELS = ['Matriculation', 'Intermediate'];
export const CGPA_LEVELS = ['Bachelors', 'Masters'];
export const OTHER_LEVEL = 'Other';
export const A_LEVEL_TYPES = ['AS', 'A2'];

export const emptySubjectRow = () => ({ subject: '', grade: '' });

export const toMonthValue = (value) => {
    if (!value) return '';
    const raw = String(value).split('T')[0];
    return raw.length >= 7 ? raw.slice(0, 7) : raw;
};

export const durationFields = (entry = {}) => ({
    durationFrom: toMonthValue(entry.durationFrom || entry.duration_from || entry.start_date),
    durationTo: toMonthValue(entry.durationTo || entry.duration_to || entry.end_date)
});

export const emptyALevelStage = () => ({
    instituteName: '',
    subjects: [emptySubjectRow()],
    durationFrom: '',
    durationTo: ''
});

export const emptyALevelDetails = () => ({
    types: [],
    instituteName: '',
    subjects: [emptySubjectRow()],
    durationFrom: '',
    durationTo: '',
    stages: {
        AS: emptyALevelStage(),
        A2: emptyALevelStage()
    }
});

export const emptyQualificationDetails = () => ({
    'O Level': { subjects: [emptySubjectRow()], instituteName: '', durationFrom: '', durationTo: '' },
    'A Level': emptyALevelDetails(),
    'Matriculation': { percentage: '', instituteName: '', durationFrom: '', durationTo: '' },
    'Intermediate': { percentage: '', instituteName: '', durationFrom: '', durationTo: '' },
    'Bachelors': { cgpaDivision: '', instituteName: '', durationFrom: '', durationTo: '' },
    'Masters': { cgpaDivision: '', instituteName: '', durationFrom: '', durationTo: '' },
    'Other': { nameOrGrade: '', instituteName: '', durationFrom: '', durationTo: '' }
});

export const emptyQualificationsState = () => ({
    selected: [],
    details: emptyQualificationDetails()
});

export const createEmptyQualificationDetail = (level) => {
    if (level === 'A Level') return emptyALevelDetails();
    if (level === OTHER_LEVEL) {
        return { nameOrGrade: '', instituteName: '', durationFrom: '', durationTo: '' };
    }
    if (SUBJECT_GRADE_LEVELS.includes(level)) {
        return { subjects: [emptySubjectRow()], instituteName: '', durationFrom: '', durationTo: '' };
    }
    if (PERCENTAGE_LEVELS.includes(level)) {
        return { percentage: '', instituteName: '', durationFrom: '', durationTo: '' };
    }
    if (CGPA_LEVELS.includes(level)) {
        return { cgpaDivision: '', instituteName: '', durationFrom: '', durationTo: '' };
    }
    return { instituteName: '', durationFrom: '', durationTo: '' };
};

const qualificationInstituteName = (entry) =>
    (entry?.instituteName || entry?.institute_name || '').trim();

const mapSubjectRows = (list) => {
    if (!Array.isArray(list) || list.length === 0) return [emptySubjectRow()];
    return list.map(s => ({ subject: s.subject || '', grade: s.grade || '' }));
};

const normalizeALevelTypes = (value) => {
    const list = Array.isArray(value) ? value : (value ? [value] : []);
    return A_LEVEL_TYPES.filter(type => list.includes(type));
};

const hasSubjectContent = (subjects) =>
    (subjects || []).some(s => (s.subject || '').trim() || (s.grade || '').trim());

const applyInstituteBackfill = (state, lead) => {
    const fallback = (lead?.institute_name || '').trim();
    if (!fallback || state.selected.length !== 1) return state;
    const level = state.selected[0];
    const detail = state.details[level];
    if (!detail) return state;

    if (level === 'A Level') {
        const types = normalizeALevelTypes(detail.types);
        if (types.length === 1) {
            const stageKey = types[0];
            const stage = detail.stages?.[stageKey] || emptyALevelStage();
            if (!(stage.instituteName || '').trim()) {
                state.details[level] = {
                    ...detail,
                    stages: {
                        ...detail.stages,
                        [stageKey]: { ...stage, instituteName: fallback }
                    }
                };
            }
        } else if (!types.length && !(detail.instituteName || '').trim()) {
            state.details[level] = { ...detail, instituteName: fallback };
        }
        return state;
    }

    if (!(detail.instituteName || '').trim()) {
        state.details[level] = { ...detail, instituteName: fallback };
    }
    return state;
};

export const summarizeQualificationInstitutes = (qualifications) => {
    const names = [];
    (qualifications?.selected || []).forEach(level => {
        const detail = qualifications.details?.[level];
        if (!detail) return;
        if (level === 'A Level') {
            const types = normalizeALevelTypes(detail.types);
            types.forEach(type => {
                const name = (detail.stages?.[type]?.instituteName || '').trim();
                if (name) names.push(name);
            });
            const fallback = (detail.instituteName || '').trim();
            if (!types.length && fallback) names.push(fallback);
            return;
        }
        const name = (detail.instituteName || '').trim();
        if (name) names.push(name);
    });
    return [...new Set(names)].join(', ');
};

const parseALevelDetailsFromEntry = (entry) => {
    const subjects = Array.isArray(entry.subjects) && entry.subjects.length > 0
        ? mapSubjectRows(entry.subjects)
        : (entry.subject || entry.grade
            ? [{ subject: entry.subject || '', grade: entry.grade || '' }]
            : [emptySubjectRow()]);
    const instituteName = qualificationInstituteName(entry);
    let types = normalizeALevelTypes(entry.aLevelTypes || entry.types);

    if (!types.length && entry.stages && typeof entry.stages === 'object') {
        types = A_LEVEL_TYPES.filter(type => {
            const stage = entry.stages[type];
            if (!stage) return false;
            return qualificationInstituteName(stage) || hasSubjectContent(stage.subjects);
        });
    }

    const stages = {
        AS: emptyALevelStage(),
        A2: emptyALevelStage()
    };

    A_LEVEL_TYPES.forEach(type => {
        const stage = entry.stages?.[type];
        if (stage) {
            stages[type] = {
                instituteName: qualificationInstituteName(stage),
                subjects: mapSubjectRows(stage.subjects),
                ...durationFields(stage)
            };
        } else if (types.length === 1 && types[0] === type) {
            stages[type] = { instituteName, subjects, ...durationFields(entry) };
        }
    });

    return { types, instituteName, subjects, stages, ...durationFields(entry) };
};

const normalizeLevelName = (name) => {
    if (!name) return '';
    const map = {
        'O levels': 'O Level', 'O Levels': 'O Level', 'O Level': 'O Level',
        'A levels': 'A Level', 'A Levels': 'A Level', 'A Level': 'A Level',
        'Matric': 'Matriculation', 'Matriculation': 'Matriculation',
        'FSc': 'Intermediate', 'Intermediate': 'Intermediate',
        'Bachelors': 'Bachelors', 'Bachelor': 'Bachelors', 'Graduate': 'Bachelors',
        'Masters': 'Masters', 'Master': 'Masters', 'Post Graduate': 'Masters', 'Postgraduate': 'Masters',
        'Other': 'Other'
    };
    return map[name] || (QUALIFICATION_OPTIONS.includes(name) ? name : '');
};

export const parseQualificationsFromLead = (lead) => {
    const state = emptyQualificationsState();
    let parsed = null;

    if (lead.qualifications) {
        try {
            parsed = typeof lead.qualifications === 'string' ? JSON.parse(lead.qualifications) : lead.qualifications;
        } catch (e) { /* fall through */ }
    }

    if (Array.isArray(parsed) && parsed.length > 0) {
        if (parsed[0].level || parsed.some(q => q.subjects || q.percentage != null || q.cgpaDivision != null || q.stages || q.aLevelTypes)) {
            parsed.forEach(entry => {
                const level = normalizeLevelName(entry.level || entry.qualification);
                if (!level || !QUALIFICATION_OPTIONS.includes(level)) return;
                if (!state.selected.includes(level)) state.selected.push(level);

                if (level === 'A Level') {
                    state.details[level] = parseALevelDetailsFromEntry(entry);
                } else if (SUBJECT_GRADE_LEVELS.includes(level)) {
                    const subjects = Array.isArray(entry.subjects) && entry.subjects.length > 0
                        ? mapSubjectRows(entry.subjects)
                        : (entry.subject || entry.grade
                            ? [{ subject: entry.subject || '', grade: entry.grade || '' }]
                            : [emptySubjectRow()]);
                    state.details[level] = { subjects, instituteName: qualificationInstituteName(entry), ...durationFields(entry) };
                } else if (PERCENTAGE_LEVELS.includes(level)) {
                    state.details[level] = {
                        percentage: entry.percentage || entry.grade || '',
                        instituteName: qualificationInstituteName(entry),
                        ...durationFields(entry)
                    };
                } else if (CGPA_LEVELS.includes(level)) {
                    state.details[level] = {
                        cgpaDivision: entry.cgpaDivision || entry.grade || '',
                        instituteName: qualificationInstituteName(entry),
                        ...durationFields(entry)
                    };
                } else if (level === OTHER_LEVEL) {
                    state.details[level] = {
                        nameOrGrade: entry.nameOrGrade || entry.otherText || entry.grade || entry.subject || '',
                        instituteName: qualificationInstituteName(entry),
                        ...durationFields(entry)
                    };
                }
            });
            return applyInstituteBackfill(state, lead);
        }

        const byLevel = {};
        parsed.forEach(row => {
            const level = normalizeLevelName(row.qualification);
            if (!level) return;
            if (!state.selected.includes(level)) state.selected.push(level);
            if (!byLevel[level]) byLevel[level] = [];
            if (SUBJECT_GRADE_LEVELS.includes(level)) {
                byLevel[level].push({ subject: row.subject || '', grade: row.grade || '' });
            } else             if (PERCENTAGE_LEVELS.includes(level)) {
                state.details[level] = {
                    percentage: row.grade || row.subject || '',
                    instituteName: qualificationInstituteName(row),
                    ...durationFields(row)
                };
            } else if (CGPA_LEVELS.includes(level)) {
                state.details[level] = {
                    cgpaDivision: row.grade || row.subject || '',
                    instituteName: qualificationInstituteName(row),
                    ...durationFields(row)
                };
            } else if (level === OTHER_LEVEL) {
                state.details[level] = {
                    nameOrGrade: row.grade || row.subject || row.nameOrGrade || '',
                    instituteName: qualificationInstituteName(row),
                    ...durationFields(row)
                };
            }
        });
        Object.keys(byLevel).forEach(level => {
            const source = parsed.find(row => normalizeLevelName(row.qualification) === level) || {};
            const subjects = byLevel[level].length > 0 ? byLevel[level] : [emptySubjectRow()];
            const instituteName = qualificationInstituteName(source);
            state.details[level] = level === 'A Level'
                ? parseALevelDetailsFromEntry({ ...source, subjects, instituteName })
                : { subjects, instituteName, ...durationFields(source) };
        });
        return applyInstituteBackfill(state, lead);
    }

    if (lead.qualification || lead.grades) {
        const level = normalizeLevelName(lead.qualification);
        if (level) {
            state.selected = [level];
            if (level === 'A Level') {
                state.details[level] = parseALevelDetailsFromEntry({
                    subjects: [{ subject: '', grade: lead.grades || '' }],
                    instituteName: ''
                });
            } else if (SUBJECT_GRADE_LEVELS.includes(level)) {
                state.details[level] = { subjects: [{ subject: '', grade: lead.grades || '' }], instituteName: '', durationFrom: '', durationTo: '' };
            } else if (PERCENTAGE_LEVELS.includes(level)) {
                state.details[level] = { percentage: lead.grades || '', instituteName: '', durationFrom: '', durationTo: '' };
            } else if (CGPA_LEVELS.includes(level)) {
                state.details[level] = { cgpaDivision: lead.grades || '', instituteName: '', durationFrom: '', durationTo: '' };
            } else if (level === OTHER_LEVEL) {
                state.details[level] = { nameOrGrade: lead.grades || '', instituteName: '', durationFrom: '', durationTo: '' };
            }
        }
    }
    return applyInstituteBackfill(state, lead);
};

const serializeSubjects = (subjects) =>
    (subjects || []).map(s => ({
        subject: (s.subject || '').trim(),
        grade: (s.grade || '').trim()
    }));

export const buildQualificationsForApi = (qualifications) => {
    if (!qualifications?.selected?.length) return [];
    return qualifications.selected.map(level => {
        const detail = qualifications.details?.[level] || {};
        if (level === 'A Level') {
            const types = normalizeALevelTypes(detail.types);
            const stages = {};
            types.forEach(type => {
                const stage = detail.stages?.[type] || emptyALevelStage();
                stages[type] = {
                    instituteName: (stage.instituteName || '').trim(),
                    subjects: serializeSubjects(stage.subjects),
                    durationFrom: toMonthValue(stage.durationFrom),
                    durationTo: toMonthValue(stage.durationTo)
                };
            });
            const instituteName = types.length
                ? [...new Set(types.map(type => stages[type].instituteName).filter(Boolean))].join(', ')
                : (detail.instituteName || '').trim();
            const payload = {
                level,
                aLevelTypes: types,
                instituteName,
                stages,
                durationFrom: toMonthValue(detail.durationFrom),
                durationTo: toMonthValue(detail.durationTo)
            };
            if (!types.length) {
                payload.subjects = serializeSubjects(detail.subjects);
            }
            return payload;
        }
        const instituteName = (detail.instituteName || '').trim();
        if (SUBJECT_GRADE_LEVELS.includes(level)) {
            return {
                level,
                instituteName,
                subjects: serializeSubjects(detail.subjects),
                durationFrom: toMonthValue(detail.durationFrom),
                durationTo: toMonthValue(detail.durationTo)
            };
        }
        if (PERCENTAGE_LEVELS.includes(level)) {
            return {
                level,
                instituteName,
                percentage: (detail.percentage || '').trim(),
                durationFrom: toMonthValue(detail.durationFrom),
                durationTo: toMonthValue(detail.durationTo)
            };
        }
        if (CGPA_LEVELS.includes(level)) {
            return {
                level,
                instituteName,
                cgpaDivision: (detail.cgpaDivision || '').trim(),
                durationFrom: toMonthValue(detail.durationFrom),
                durationTo: toMonthValue(detail.durationTo)
            };
        }
        if (level === OTHER_LEVEL) {
            return {
                level,
                nameOrGrade: (detail.nameOrGrade || '').trim(),
                instituteName,
                durationFrom: toMonthValue(detail.durationFrom),
                durationTo: toMonthValue(detail.durationTo)
            };
        }
        return {
            level,
            instituteName,
            durationFrom: toMonthValue(detail.durationFrom),
            durationTo: toMonthValue(detail.durationTo)
        };
    });
};

export const toggleALevelType = (detail, type) => {
    if (!A_LEVEL_TYPES.includes(type)) return detail;
    const current = {
        ...emptyALevelDetails(),
        ...detail,
        stages: {
            AS: { ...emptyALevelStage(), ...(detail?.stages?.AS || {}) },
            A2: { ...emptyALevelStage(), ...(detail?.stages?.A2 || {}) }
        }
    };
    const types = normalizeALevelTypes(current.types);
    const isSelected = types.includes(type);
    const nextTypes = isSelected
        ? types.filter(item => item !== type)
        : A_LEVEL_TYPES.filter(item => item === type || types.includes(item));

    if (!isSelected) {
        const stage = current.stages[type] || emptyALevelStage();
        const stageEmpty = !(stage.instituteName || '').trim() && !hasSubjectContent(stage.subjects);
        const hasLegacy = (current.instituteName || '').trim() || hasSubjectContent(current.subjects);
        if (stageEmpty && hasLegacy) {
            current.stages[type] = {
                instituteName: current.instituteName || '',
                subjects: mapSubjectRows(current.subjects),
                durationFrom: current.durationFrom || '',
                durationTo: current.durationTo || ''
            };
        }
    }

    return { ...current, types: nextTypes };
};

export const changeSubjectRow = (detail, index, field, value, stage) => {
    if (stage) {
        const stageData = detail.stages?.[stage] || emptyALevelStage();
        const subjects = [...(stageData.subjects || [])];
        subjects[index] = { ...(subjects[index] || emptySubjectRow()), [field]: value };
        return {
            ...detail,
            stages: {
                ...(detail.stages || {}),
                [stage]: { ...stageData, subjects }
            }
        };
    }
    const subjects = [...(detail.subjects || [])];
    subjects[index] = { ...(subjects[index] || emptySubjectRow()), [field]: value };
    return { ...detail, subjects };
};

export const addSubjectRowToDetail = (detail, stage) => {
    if (stage) {
        const stageData = detail.stages?.[stage] || emptyALevelStage();
        return {
            ...detail,
            stages: {
                ...(detail.stages || {}),
                [stage]: {
                    ...stageData,
                    subjects: [...(stageData.subjects || []), emptySubjectRow()]
                }
            }
        };
    }
    return { ...detail, subjects: [...(detail.subjects || []), emptySubjectRow()] };
};

export const removeSubjectRowFromDetail = (detail, index, stage) => {
    const nextSubjects = (subjects) => {
        const list = subjects || [];
        return list.length > 1 ? list.filter((_, i) => i !== index) : [emptySubjectRow()];
    };
    if (stage) {
        const stageData = detail.stages?.[stage] || emptyALevelStage();
        return {
            ...detail,
            stages: {
                ...(detail.stages || {}),
                [stage]: { ...stageData, subjects: nextSubjects(stageData.subjects) }
            }
        };
    }
    return { ...detail, subjects: nextSubjects(detail.subjects) };
};

export const changeALevelStageField = (detail, stage, field, value) => {
    const stageData = detail.stages?.[stage] || emptyALevelStage();
    return {
        ...detail,
        stages: {
            ...(detail.stages || {}),
            [stage]: { ...stageData, [field]: value }
        }
    };
};

export const hasLegacyALevelFields = (detail) =>
    Boolean((detail?.instituteName || '').trim() || hasSubjectContent(detail?.subjects));

const parseStoredEducationPayload = (value) => {
    const empty = { subjects: [emptySubjectRow()], aLevelType: null };
    const fromParsed = (parsed) => {
        if (Array.isArray(parsed)) {
            return { subjects: mapSubjectRows(parsed), aLevelType: null };
        }
        if (parsed && typeof parsed === 'object') {
            const aLevelType = A_LEVEL_TYPES.includes(parsed.aLevelType) ? parsed.aLevelType : null;
            return {
                subjects: mapSubjectRows(parsed.subjects || []),
                aLevelType
            };
        }
        return empty;
    };

    if (Array.isArray(value) || (value && typeof value === 'object')) {
        return fromParsed(value);
    }
    if (!value) return empty;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return empty;
        try {
            return fromParsed(JSON.parse(trimmed));
        } catch {
            const subjects = trimmed.split(',').map(subject => ({ subject: subject.trim(), grade: '' })).filter(s => s.subject);
            return { subjects: subjects.length ? subjects : [emptySubjectRow()], aLevelType: null };
        }
    }
    return empty;
};

const summarizeSubjectGrades = (subjects) =>
    (subjects || [])
        .filter(s => (s.subject || '').trim() || (s.grade || '').trim())
        .map(s => [s.subject, s.grade].filter(Boolean).join(' '))
        .join(', ');

const educationRowFromDetail = (level, detail) => {
    const subjects = SUBJECT_GRADE_LEVELS.includes(level) || level === 'A Level'
        ? serializeSubjects(detail?.subjects)
        : [];
    let result = '';
    if (PERCENTAGE_LEVELS.includes(level)) result = (detail?.percentage || '').trim();
    else if (CGPA_LEVELS.includes(level)) result = (detail?.cgpaDivision || '').trim();
    else if (level === OTHER_LEVEL) result = (detail?.nameOrGrade || '').trim();
    else result = summarizeSubjectGrades(detail?.subjects);

    const aLevelType = A_LEVEL_TYPES.includes(detail?.aLevelType) ? detail.aLevelType : null;
    const subjectsValue = aLevelType
        ? JSON.stringify({ aLevelType, subjects })
        : (subjects.length ? JSON.stringify(subjects) : '');

    return {
        education_level: level,
        institute_name: (detail?.instituteName || '').trim(),
        start_date: toMonthValue(detail?.durationFrom),
        end_date: toMonthValue(detail?.durationTo),
        subjects: subjectsValue,
        grade_cgpa: result,
        result
    };
};

export const qualificationsToEducationRows = (qualifications) => {
    const rows = [];
    (qualifications?.selected || []).forEach(level => {
        const detail = qualifications.details?.[level] || {};
        if (level === 'A Level') {
            const types = normalizeALevelTypes(detail.types);
            if (types.length) {
                types.forEach(type => {
                    rows.push(educationRowFromDetail('A Level', {
                        ...(detail.stages?.[type] || emptyALevelStage()),
                        aLevelType: type
                    }));
                });
                return;
            }
            rows.push(educationRowFromDetail(level, detail));
            return;
        }
        rows.push(educationRowFromDetail(level, detail));
    });
    return rows.filter(row =>
        row.education_level &&
        (row.institute_name || row.subjects || row.grade_cgpa || row.start_date || row.end_date)
    );
};

const splitALevelLevel = (name) => {
    const raw = String(name || '').trim();
    const match = raw.match(/^A Level\s+(AS|A2)$/i);
    if (match) return { level: 'A Level', type: match[1].toUpperCase() === 'A2' ? 'A2' : 'AS' };
    return { level: normalizeLevelName(raw), type: null };
};

export const parseQualificationsFromEducation = (rows) => {
    const state = emptyQualificationsState();
    if (!Array.isArray(rows) || rows.length === 0) return state;

    rows.forEach(row => {
        const { level, type: typeFromName } = splitALevelLevel(row.education_level);
        if (!level || !QUALIFICATION_OPTIONS.includes(level)) return;
        if (!state.selected.includes(level)) state.selected.push(level);

        const { subjects, aLevelType } = parseStoredEducationPayload(row.subjects);
        const type = aLevelType || typeFromName;
        const durations = durationFields(row);
        const instituteName = (row.institute_name || '').trim();
        const result = (row.grade_cgpa || row.result || '').trim();

        if (level === 'A Level') {
            const current = {
                ...emptyALevelDetails(),
                ...(state.details[level] || {})
            };
            if (type) {
                const types = normalizeALevelTypes([...(current.types || []), type]);
                current.types = types;
                current.stages = {
                    ...emptyALevelDetails().stages,
                    ...current.stages,
                    [type]: {
                        instituteName,
                        subjects: hasSubjectContent(subjects) ? subjects : [emptySubjectRow()],
                        ...durations
                    }
                };
            } else {
                current.instituteName = instituteName;
                current.subjects = hasSubjectContent(subjects) ? subjects : current.subjects;
                current.durationFrom = durations.durationFrom;
                current.durationTo = durations.durationTo;
            }
            state.details[level] = current;
            return;
        }

        if (SUBJECT_GRADE_LEVELS.includes(level)) {
            state.details[level] = {
                subjects: hasSubjectContent(subjects)
                    ? subjects
                    : (result ? [{ subject: '', grade: result }] : [emptySubjectRow()]),
                instituteName,
                ...durations
            };
            return;
        }
        if (PERCENTAGE_LEVELS.includes(level)) {
            state.details[level] = { percentage: result, instituteName, ...durations };
            return;
        }
        if (CGPA_LEVELS.includes(level)) {
            state.details[level] = { cgpaDivision: result, instituteName, ...durations };
            return;
        }
        if (level === OTHER_LEVEL) {
            state.details[level] = { nameOrGrade: result, instituteName, ...durations };
        }
    });

    return state;
};
